import { FC, useState } from 'react';

import { useRoomSettings } from '@features/roomSettings';
import { gridTheme } from '@utils/grid';
import moment, { Moment } from 'moment-timezone';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { useRouter } from 'shared-routing';
import { JourneyParams } from 'shared-types/Common';
import { RoomRateAvailability } from 'shared-types/Room';
import { Routes } from '../../constants';
import { useIsWindowWiderThan } from '../../hooks';
import { CalendarDay } from './CalendarDay';
import { roomRateAvailabilityStatuses } from './constants';
import { DesktopInput } from './DesktopInput';
import { getMatchingDay } from './helpers';

import { StyledDateRangePickerWrapper } from './DateRangeInput.styles';

import { MobileInput } from './MobileInput';

type DateRangeInputProps = {
  isSingle?: boolean;
  availability?: RoomRateAvailability;
  propertyId?: number;
  journeyParams?: JourneyParams;
  fetchAvailability?: (
    propertyId: number,
    journeyParams: JourneyParams,
    extendedDate: Moment,
    useRateCode?: boolean,
    rateCode?: string,
    roomCode?: string,
    hideCalendarPricing?: boolean
  ) => void;
  noAvailability?: boolean;
  startDate: Moment;
  endDate: Moment;
  onChange: (any) => void;
  rateCode?: string;
  roomCode?: string;
  isHomepage?: boolean;
};

export enum DateType {
  STARTDATE = 'startDate',
  ENDDATE = 'endDate',
  NONE = '',
}

export const DateRangeInput: FC<DateRangeInputProps> = ({
  startDate,
  endDate,
  onChange,
  isSingle,
  availability = [],
  propertyId,
  journeyParams,
  fetchAvailability,
  rateCode = '',
  roomCode = '',
  isHomepage = false,
}) => {
  const { AVAILABLE, CHECK_IN_CLOSED } = roomRateAvailabilityStatuses;
  const [dateType, setDateType] = useState<DateType>(DateType.NONE);
  const [currentSelectedDate, setCurrentSelectedDate] = useState(
    getMatchingDay(availability, startDate)
  );

  const [showMessage, setShowMessage] = useState(true);
  const { data: roomSettings } = useRoomSettings();

  const showNoAvailabilityMessage =
    availability?.length && !availability?.find((day) => day.type === AVAILABLE);

  const isWiderViewport = useIsWindowWiderThan(gridTheme.breakpoints.md);

  const router = useRouter();

  const useRateCode = [Routes.RATE_LIST, Routes.RATE_DETAIL].includes(router?.pathname as Routes);

  const handleDatesChange = ({ startDate, endDate }) => {
    setShowMessage(true);
    const currentStartDateAvailability = getMatchingDay(availability, startDate);
    setCurrentSelectedDate(currentStartDateAvailability);
    const onChangeEndDate =
      !endDate || moment(endDate).isSameOrBefore(moment(startDate))
        ? moment(startDate).add(1, 'day')
        : endDate;

    onChange({ startDate: moment(startDate), endDate: onChangeEndDate });
  };

  const handleDateChange = (startDate) => {
    onChange({ startDate, endDate: undefined });
  };

  const handleOnClose = () => {
    if (!endDate) {
      onChange({
        startDate,
        endDate: moment(startDate).add(1, 'days'),
      });
    }
  };

  const unavailableDays = availability?.filter(
    (day) => !(day.type === AVAILABLE || day.type === CHECK_IN_CLOSED)
  );

  const dayBeforeCurrentDate = (day) => day.isBefore(moment(), 'day');

  const dayBeforeStartDate = (day, startDate) => day.isBefore(moment(startDate), 'day');

  const nextUnavailableDay = unavailableDays?.find((day) =>
    moment(day.date).isAfter(moment(startDate), 'day')
  );

  const isDayUnavailable = (day) =>
    unavailableDays?.find(
      (unavailableDay) =>
        !(dateType === 'endDate' && unavailableDay === nextUnavailableDay) &&
        day.isSame(moment(unavailableDay.date), 'day') &&
        !day.isSame(endDate)
    );

  const dayIsUnavailable = (day) =>
    isDayUnavailable(day) ||
    dayBeforeCurrentDate(day) ||
    (dateType === 'endDate' && dayBeforeStartDate(day, startDate)) ||
    (dateType === 'endDate' && nextUnavailableDay && day.isAfter(nextUnavailableDay.date, 'day'));

  const closedCheckInDays = availability?.filter((day) => day.type === CHECK_IN_CLOSED);

  const dayIsHighlighted = (day) =>
    closedCheckInDays?.find((closedCheckInDay) => day.isSame(moment(closedCheckInDay.date), 'day'));

  const extendedDay = (day) => (
    <CalendarDay day={getMatchingDay(availability, day) || { date: day }} />
  );

  const getAvailability = (dayOfMonth) => {
    const extendedDate = moment(dayOfMonth);
    if (fetchAvailability && propertyId && journeyParams) {
      fetchAvailability(
        propertyId,
        journeyParams,
        extendedDate,
        useRateCode,
        rateCode,
        roomCode,
        roomSettings?.data?.hideCalendarPricing || false
      );
    }
  };

  return (
    <StyledDateRangePickerWrapper>
      {isWiderViewport ? (
        <DesktopInput
          currentSelectedDate={currentSelectedDate}
          dateType={dateType}
          dayIsHighlighted={dayIsHighlighted}
          dayIsUnavailable={dayIsUnavailable}
          endDate={endDate}
          extendedDay={extendedDay}
          getAvailability={getAvailability}
          handleDateChange={handleDateChange}
          handleDatesChange={handleDatesChange}
          handleOnClose={handleOnClose}
          isSingle={isSingle}
          setDateType={setDateType}
          setShowMessage={setShowMessage}
          showMessage={showMessage}
          showNoAvailabilityMessage={showNoAvailabilityMessage}
          startDate={startDate}
        />
      ) : (
        <MobileInput
          currentSelectedDate={currentSelectedDate}
          dateType={dateType}
          dayIsHighlighted={dayIsHighlighted}
          dayIsUnavailable={dayIsUnavailable}
          endDate={endDate}
          extendedDay={extendedDay}
          getAvailability={getAvailability}
          handleDateChange={handleDateChange}
          handleDatesChange={handleDatesChange}
          handleOnClose={handleOnClose}
          isSingle={isSingle}
          setDateType={setDateType}
          setShowMessage={setShowMessage}
          showMessage={showMessage}
          showNoAvailabilityMessage={showNoAvailabilityMessage}
          startDate={startDate}
          isHomepage={isHomepage}
        />
      )}
    </StyledDateRangePickerWrapper>
  );
};
